import { useCallback, useEffect, useState } from "react";
import { ChatNotificationGate, type IncomingChatNotification } from "@/utils/chatNotificationGate";

interface NotificationAccount { channel: "marketplace" | "customer_care" | "activity"; role: "user" | "vendor"; id: string }
const gates = new Map<string, ChatNotificationGate>();
let audio: HTMLAudioElement | null = null;
let audioUnlocked = false;
let unlocking = false;
let subscribers = 0;
let lastPlayback = -Infinity;
const sound = () => {
  if (!audio) { audio = new Audio("/sounds/support-notification.wav"); audio.preload = "auto"; audio.volume = 0.25; }
  return audio;
};
const unlock = (event: Event) => {
  if (!event.isTrusted || audioUnlocked || unlocking) return;
  try {
    const player = sound();
    unlocking = true;
    player.muted = true;
    void player.play().then(() => {
      player.pause(); player.currentTime = 0; player.muted = false;
      audioUnlocked = true; unlocking = false;
    }).catch(() => { player.muted = false; unlocking = false; });
  } catch { unlocking = false; }
};
const readMuted = (key: string) => { try { return localStorage.getItem(key) === "true"; } catch { return false; } };

export const useChatNotifications = (account: NotificationAccount) => {
  const namespace = account.channel + ":" + account.role + ":" + account.id;
  const preferenceKey = "mbaay:notifications:" + namespace + ":muted";
  const [preference, setPreference] = useState(() => ({ key: preferenceKey, muted: readMuted(preferenceKey) }));
  const muted = preference.key === preferenceKey ? preference.muted : readMuted(preferenceKey);
  if (!gates.has(namespace)) gates.set(namespace, new ChatNotificationGate(account.id));
  const gate = gates.get(namespace)!;

  useEffect(() => {
    if (subscribers++ === 0) {
      document.addEventListener("pointerdown", unlock, { passive: true });
      document.addEventListener("keydown", unlock);
    }
    return () => {
      if (--subscribers === 0) {
        document.removeEventListener("pointerdown", unlock);
        document.removeEventListener("keydown", unlock);
        audio?.pause();
      }
    };
  }, []);

  useEffect(() => {
    const update = () => setPreference({ key: preferenceKey, muted: readMuted(preferenceKey) });
    const onStorage = (event: StorageEvent) => { if (event.key === preferenceKey || event.key === null) update(); };
    update();
    window.addEventListener("storage", onStorage);
    window.addEventListener("mbaay:notification-preference", update);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("mbaay:notification-preference", update);
    };
  }, [preferenceKey]);

  const toggleMuted = useCallback(() => {
    const next = !muted;
    setPreference({ key: preferenceKey, muted: next });
    try {
      localStorage.setItem(preferenceKey, String(next));
      window.dispatchEvent(new Event("mbaay:notification-preference"));
    } catch { /* The current tab still respects mute. */ }
  }, [muted, preferenceKey]);

  const notifyIncoming = useCallback((message: IncomingChatNotification) => {
    const now = Date.now();
    if (!gate.shouldNotify(message, { muted, unlocked: audioUnlocked, now }) || now - lastPlayback < 1500) return;
    try {
      const player = sound();
      player.currentTime = 0;
      lastPlayback = now;
      void player.play().catch(() => { audioUnlocked = false; });
    } catch { /* Delivery must continue when browser audio is unavailable. */ }
  }, [gate, muted]);
  const rememberMessages = useCallback((messages: readonly Pick<IncomingChatNotification, "id" | "chatId">[]) => gate.rememberMessages(messages), [gate]);
  return { muted, toggleMuted, notifyIncoming, rememberMessages };
};
